import { LitElement, html, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import styles from './csv-import-confirm-body.css?inline';

import '@inmar/component-lib';
import '../csv-import-validation-summary/csv-import-validation-summary.js';

import type { ConfirmView } from '../../types/views';
import {
  EMPTY_VALIDATION_SUMMARY,
  type ReviewValidationSummary,
} from '../csv-import-validation-summary/csv-import-validation-summary.js';

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementTagNameMap {
    'csv-import-confirm-body': CsvImportConfirmBody;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Body content for the "Confirm" step of the CSV Import flow.
 *
 * Displays import results: a success banner or error alert, a summary card
 * with valid/error/duplicate counts, and an optional "Final error report"
 * download button. Manages focus and aria-live announcements for accessibility.
 */
@customElement('csv-import-confirm-body')
export class CsvImportConfirmBody extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  /** The fully-resolved view state, computed by the orchestrator. */
  @property({ type: String })
  view: ConfirmView = 'success';

  /**
   * Summary counts for the import result.
   * The orchestrator passes a fresh object when counts change.
   */
  @property({ type: Object, attribute: false })
  summary: ReviewValidationSummary = EMPTY_VALIDATION_SUMMARY;

  /** Screen-reader announcement for view transitions. */
  @state() private _announcement: string | null = null;

  /** Previous view for detecting transitions in willUpdate. */
  @state() private _previousView: ConfirmView | null = null;

  // --------------------------------------------------------------------------
  // Lifecycle
  // --------------------------------------------------------------------------

  override willUpdate(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has('view') && this._previousView !== null) {
      if (this.view === 'import-report-download-error') {
        this._announcement = 'Error report download failed. Retry available.';
      } else if (this._previousView === 'import-report-download-error') {
        this._announcement = 'Import summary displayed.';
      }
    }
    this._previousView = this.view;
  }

  override firstUpdated(): void {
    this.renderRoot.querySelector<HTMLElement>('h2')?.focus();
  }

  // --------------------------------------------------------------------------
  // Rendering
  // --------------------------------------------------------------------------

  override render() {
    return html`
      <div class="body">
        <h2 tabindex="-1" class="heading sr-only">Import Results</h2>
        ${this.view === 'import-report-download-error'
          ? html`<div role="alert" class="error-alert">
              <div class="error-alert-heading">
                <span class="error-alert-icon" aria-hidden="true">
                  <hcds-icon
                    name="diamond-exclamation"
                    family="classic"
                    icon-style="light"
                  ></hcds-icon>
                </span>
                <p class="error-alert-title">
                  The error report could not be downloaded.
                </p>
              </div>
              <p class="error-alert-message">
                Please try again or submit a help ticket.
              </p>
            </div>`
          : html`<div role="status" class="success-banner">
              <span class="success-banner-icon" aria-hidden="true">
                <hcds-icon
                  name="check-circle"
                  family="classic"
                  icon-style="light"
                ></hcds-icon>
              </span>
              <span class="success-banner-text">
                Your import is complete.
                <strong>${this.summary.validCount} records</strong> have been
                imported.
              </span>
            </div>`}
        ${this._renderSummaryCard()}
        <div aria-live="polite" aria-atomic="true" class="sr-only">
          ${this._announcement ?? ''}
        </div>
      </div>
    `;
  }

  /**
   * Renders the summary card via the shared csv-import-validation-summary
   * component, slotting in the "Final error report" button when applicable.
   */
  private _renderSummaryCard() {
    return html`
      <csv-import-validation-summary .summary=${this.summary}>
        ${this.view !== 'success'
          ? html`<inmar-hc-button
              slot="actions"
              label="Final error report"
              variant="secondary"
              @button-click=${this._onDownloadClick}
            >
              <hcds-icon
                slot="left-icon"
                name="download"
                family="classic"
                icon-style="light"
              ></hcds-icon>
            </inmar-hc-button>`
          : nothing}
      </csv-import-validation-summary>
    `;
  }

  // --------------------------------------------------------------------------
  // Event handlers
  // --------------------------------------------------------------------------

  private _onDownloadClick() {
    this.dispatchEvent(
      new CustomEvent('csv-import-download-confirm-report', {
        bubbles: true,
        composed: true,
      }),
    );
  }
}
